import { durationWithLetters } from 'webui/utils/time';
import { owaMultiplier } from 'console/device-settings/owa';
import { binarySearchByIndex0 } from 'webui/utils/binary-search';
import { add_gaps, make_staircase, normalize_time, time_mul_1k, } from './common';
import { BooleanMarkingsGraphSource, BottomMarkingsSeries, GraphSource, LineSeries, TemperatureGraphData, TooltipSeries, } from './base';
import { HeatingCircuit } from 'z3k-config';
import { LoadDataType } from '../load-types';
import { owaAxis } from 'console/tabs/graphs/components/graph/utils/owa-axes';
function find_by_id(array, id) {
    for (const obj of array != null ? array : []) {
        if (obj.id === id) {
            return obj;
        }
    }
    return null;
}
export class TemperatureSensorGraphData extends TemperatureGraphData {
    class_name() {
        return 'TemperatureSensorGraphData';
    }
    device;
    therm_id;
    constructor(device, { therm_id, color }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this.device = device;
        this.therm_id = therm_id;
        this._color = color;
    }
    _therm_spec() {
        return this.device.thermometerById(this.therm_id) ?? {};
    }
    serialize_params() {
        return { therm_id: this.therm_uuid(), color: this._color };
    }
    is_same(other) {
        return (this.constructor === other.constructor &&
            this.therm_uuid() === other.therm_uuid());
    }
    yaxis_key() {
        return owaAxis(this._therm_spec().serial) ?? 'temperature';
    }
    title() {
        return this._therm_spec().is_assigned_to_slot
            ? this._therm_spec().name
            : `${this._therm_spec().name} (${this._therm_spec().serial.replace(/%/g, '')})`;
    }
    color() {
        if (this._color)
            return this._color;
        return this._therm_spec().color;
    }
    data_source_key() {
        return LoadDataType.temperature;
    }
    show_in_legend(data) {
        return data?.length > 0;
    }
    therm_uuid() {
        return this._therm_spec().uuid;
    }
    extract_data(raw_data) {
        return raw_data[this.therm_uuid()]?.temperature;
    }
    normalize_data(data) {
        const result = super.normalize_data(data);
        const mult = owaMultiplier(this._therm_spec());
        if (mult === 1)
            return result;
        return result.map(([time, value]) => [
            time,
            value != null ? value * mult : null,
        ]);
    }
}
export class TargetTempGraphSource extends GraphSource {
    class_name() {
        return 'TargetTempGraphSource';
    }
    title() {
        return 'целевая t°';
    }
    color() {
        if (this._color)
            return this._color;
        return '#8ababe';
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    yaxis_key() {
        return 'temperature';
    }
    extract_data(raw_data) {
        return raw_data?.target_temp;
    }
    normalize_data(data) {
        return make_staircase(time_mul_1k(normalize_time(data, 0)));
    }
    series_from_norm_data(xbounds, data) {
        return [
            new LineSeries({
                title: this.title(),
                color: this.color(),
                data,
                yaxis_key: this.yaxis_key(),
                shadow_size: 0,
            }),
        ];
    }
}
const zone_suffix = (multiple_zones, control_therm, zone_no, thermostat_zones_settings) => {
    if (!multiple_zones) {
        return '';
    }
    return (' (' +
        (thermostat_zones_settings?.[zone_no]?.name ??
            control_therm?.name ??
            `контур №${zone_no}`) +
        ')');
};
export class ZoneTargetTemp extends GraphSource {
    class_name() {
        return 'ZoneTargetTemp';
    }
    zone_no;
    _multiple_zones;
    _control_therm;
    _colors;
    _thermostat_zones_settings;
    constructor(device, { zone_no, color }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this.zone_no = zone_no;
        this._multiple_zones = device.has_multiple_thermostat_zones();
        this._control_therm = device.control_thermometer_for_zone(this.zone_no);
        this._color = color;
        this._thermostat_zones_settings = device.thermostat_zones_settings();
        this._colors = [
            '#8ababe',
            '#e55bce',
            '#e58900',
            '#67d67d',
            '#00d6b1',
            '#d65371',
        ];
    }
    serialize_params() {
        return { zone_no: this.zone_no, color: this._color };
    }
    is_same(other) {
        return this.constructor === other.constructor && this.zone_no === other.zone_no;
    }
    title() {
        return `целевая t°${zone_suffix(this._multiple_zones, this._control_therm, this.zone_no, this._thermostat_zones_settings)}`;
    }
    color() {
        if (this._color) {
            return this._color;
        }
        return this._colors[this.zone_no - 1];
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    yaxis_key() {
        return 'temperature';
    }
    //@ts-ignore
    extract_data(raw_data) {
        return raw_data?.zones?.[this.zone_no]?.target_temp;
    }
    normalize_data(data) {
        return make_staircase(time_mul_1k(normalize_time(data, 0)));
    }
    series_from_norm_data(xbounds, data) {
        return [
            new LineSeries({
                title: this.title(),
                color: this.color(),
                data,
                yaxis_key: this.yaxis_key(),
                shadow_size: 0,
            }),
        ];
    }
}
export class DHWTargetTemp extends GraphSource {
    class_name() {
        return 'DHWTargetTemp';
    }
    title() {
        return 'целевая t° ГВС';
    }
    color() {
        if (this._color)
            return this._color;
        return '#d66433';
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    yaxis_key() {
        return 'temperature';
    }
    extract_data(raw_data) {
        return raw_data?.dhw_t;
    }
    normalize_data(data) {
        return make_staircase(time_mul_1k(normalize_time(data, 0)));
    }
    series_from_norm_data(xbounds, data) {
        return [
            new LineSeries({
                title: this.title(),
                color: this.color(),
                yaxis_key: this.yaxis_key(),
                shadow_size: 0,
                data,
            }),
        ];
    }
}
export class PZATempGraphSource extends TemperatureGraphData {
    class_name() {
        return 'PZATempGraphSource';
    }
    title() {
        return 't° ПЗА';
    }
    color() {
        if (this._color)
            return this._color;
        return '#4b4bcb';
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    extract_data(raw_data) {
        return raw_data?.pza_t;
    }
}
export class WeatherGraphSource extends TemperatureGraphData {
    class_name() {
        return 'WeatherGraphSource';
    }
    title() {
        return 't° с погодного сервера';
    }
    color() {
        if (this._color)
            return this._color;
        return '#7851a9';
    }
    curvedLines() {
        return { apply: true, monotonicFit: true };
    }
    data_source_key() {
        return LoadDataType.weather;
    }
    extract_data(raw_data) {
        ////????????????????????????????
        return raw_data;
    }
    normalize_data(data) {
        return time_mul_1k(add_gaps(normalize_time(data, 0), 6 * 60 * 60));
    }
}
export class WorkTimeSeries extends BottomMarkingsSeries {
    norm_data;
    _object_id;
    _device;
    constructor({ norm_data, object_id, device }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._device = device;
        this.norm_data = norm_data;
        this._object_id = object_id;
    }
    stats_html = (xbounds) => {
        const data = this.norm_data;
        if (data == null)
            return null;
        const { r: l } = binarySearchByIndex0(data, xbounds[0] / 1e3);
        const { r } = binarySearchByIndex0(data, xbounds[1] / 1e3);
        let total_wt = 0;
        for (const [time, wt] of data.slice(l, r))
            total_wt += wt;
        const right_edge = Math.min(xbounds[1], new Date().getTime());
        const total_secs = ~~((right_edge - xbounds[0]) / 1e3);
        const wt_text = durationWithLetters(total_wt, {
            pad2_except_first: true,
            fallback_to_seconds: true,
        });
        const percent = Math.min(100, ~~((total_wt / total_secs) * 100));
        let title_icon = 'flame';
        if (this._object_id) {
            const object = find_by_id(this._device.z3k_config()?.heating_circuits, this._object_id);
            title_icon =
                object?.type === HeatingCircuit.Type.BOILER ? 'flame' : 'heat-request';
        }
        // noinspection CheckTagEmptyBody
        return `<span class='ui-icon-graph-stats-${title_icon}'></span> ${wt_text} (${percent}%)`;
    };
}
export class BaseBoilerWorkTime extends GraphSource {
    _work_color;
    _bg_color;
    _object_id;
    _device;
    constructor(device, { object_id }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._device = device;
        this._object_id = object_id;
        this._work_color = '#c4443b';
        this._bg_color = '#e0e0e0';
    }
    title() {
        return 'время работы котла';
    }
    show_in_legend() {
        return true;
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    color() {
        return this._color ?? this._work_color;
    }
    normalize_data(data) {
        return normalize_time(data, 0);
    }
    create_series() {
        return null;
    }
    series_from_norm_data(xbounds, norm_data) {
        return [
            new WorkTimeSeries({
                title: this.title(),
                show_in_legend: this.show_in_legend(),
                color: this.color(),
                object_id: this._object_id,
                norm_data,
                device: this._device,
                data: this.create_bottom_markings(xbounds, norm_data),
            }),
        ];
    }
    _add_to_markings(markings, time, worktime) {
        if (worktime > 0) {
            const last = markings[markings.length - 1];
            if (last != null && time * 1e3 - last.xaxis.to < 2 * 60e3) {
                last.xaxis.to = (time - 60 + worktime) * 1e3;
            }
            else {
                markings.push({
                    xaxis: {
                        from: (time - worktime) * 1e3,
                        to: time * 1e3,
                    },
                    color: this.color(),
                });
            }
        }
    }
    create_bottom_markings(xbounds, data) {
        const markings = [];
        for (const [time, wt] of data)
            this._add_to_markings(markings, time, wt);
        return markings;
    }
}
export class BoilerWorkGraphSource extends BaseBoilerWorkTime {
    class_name() {
        return 'BoilerWorkGraphSource';
    }
    extract_data(raw_data) {
        return raw_data?.boiler_work_time;
    }
}
export class ZoneWorkTime extends BaseBoilerWorkTime {
    class_name() {
        return 'ZoneWorkTime';
    }
    zone_no;
    _multiple_zones;
    _control_therm;
    _thermostat_zones_settings;
    constructor(device, { zone_no, color }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this.zone_no = zone_no;
        this._multiple_zones = device.has_multiple_thermostat_zones();
        this._control_therm = device.control_thermometer_for_zone(this.zone_no);
        this._color = color;
        this._thermostat_zones_settings = device.thermostat_zones_settings();
    }
    serialize_params() {
        return { zone_no: this.zone_no, color: this._color };
    }
    is_same(other) {
        return this.constructor === other.constructor && this.zone_no === other.zone_no;
    }
    title() {
        return `работа контура${zone_suffix(this._multiple_zones, this._control_therm, this.zone_no, this._thermostat_zones_settings)}`;
    }
    //@ts-ignore
    extract_data(raw_data) {
        const new_data = normalize_time(raw_data?.zones?.[this.zone_no]?.worktime ?? [], 0);
        if (this.zone_no !== 1)
            return new_data;
        const old_data = normalize_time(raw_data?.boiler_work_time ?? [], 0);
        if (new_data[0] == null)
            return old_data;
        const oldest_new = new_data[0][0];
        const old_data_part = old_data.filter((item) => item[0] < oldest_new);
        return old_data_part.concat(new_data);
    }
    normalize_data(data) {
        return data;
    }
}
export class ThermostatModeGraphSource extends GraphSource {
    class_name() {
        return 'ThermostatModeGraphSource';
    }
    title() {
        return 'режим термостата';
    }
    show_in_legend() {
        return false;
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    extract_data(raw_data) {
        return raw_data?.thermostat_mode;
    }
    normalize_data(data) {
        return normalize_time(data, 0);
    }
    create_series() {
        return null;
    }
    series_from_norm_data(xbounds, norm_data) {
        return [
            new TooltipSeries({
                data: norm_data,
                html_formatter: (value) => this._mode_name(value),
            }),
        ];
    }
    _device;
    constructor(_device, color) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._device = _device;
        this._color = color;
    }
    _mode_name(mode_no) {
        if (this._device.thermostat_ext_modes_config() != null) {
            const mode = this._device.thermostat_ext_modes_config()?.[mode_no];
            if (mode?.active) {
                return mode?.name;
            }
            return ''; //??????????????????/
        }
        else {
            switch (mode_no) {
                case 0:
                    if (this._device.thermostat_mode_temps()?.full_off)
                        return 'выключен';
                    return 'антизаморозка';
                case 1:
                    return 'комфорт';
                case 2:
                    return 'эконом';
                case 3:
                    return 'расписание';
                default:
                    return ''; //??????????????????/
            }
        }
    }
}
export class BoilerFailGraphSource extends BooleanMarkingsGraphSource {
    class_name() {
        return 'BoilerFailGraphSource';
    }
    title() {
        return 'авария котла';
    }
    color() {
        if (this._color)
            return this._color;
        return 'rgba(229,0,0,0.4)';
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    predicate(value) {
        return value === true;
    }
    extract_data(raw_data) {
        return raw_data?.fail;
    }
    normalize_data(data) {
        return normalize_time(data, 0);
    }
}
export class NoPowerGraphSource extends BooleanMarkingsGraphSource {
    class_name() {
        return 'NoPowerGraphSource';
    }
    title() {
        return 'отсутствие питания';
    }
    color() {
        if (this._color)
            return this._color;
        return 'rgba(0,0,229,0.2)';
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    predicate(value) {
        return value === false;
    }
    extract_data(raw_data) {
        return raw_data?.power;
    }
    normalize_data(data) {
        return normalize_time(data, 0);
    }
}
export class ThermostatGateGraphSource extends BooleanMarkingsGraphSource {
    class_name() {
        return 'ThermostatGateGraphSource';
    }
    title() {
        return 'комнатный термостат';
    }
    color() {
        if (this._color)
            return this._color;
        return 'rgba(0,200,0,0.1)';
    }
    data_source_key() {
        return LoadDataType.thermostat_work;
    }
    predicate(value) {
        return value === true;
    }
    extract_data(raw_data) {
        return raw_data?.gate;
    }
    normalize_data(data) {
        return normalize_time(data, 0);
    }
}
const heatsettempcolors = [
    '#8c2401',
    '#ba6d09',
    '#df0eea',
    '#04720d',
    '#06176d',
    '#759305',
];
export class HeatSetTempGraph extends GraphSource {
    class_name() {
        return 'HeatSetTempGraph';
    }
    zone_no;
    _multiple_zones;
    _control_therm;
    _thermostat_zones_settings;
    constructor(device, { zone_no, color }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this.zone_no = zone_no;
        this._multiple_zones = device.has_multiple_thermostat_zones();
        this._control_therm = device.control_thermometer_for_zone(this.zone_no);
        this._color = color;
        this._thermostat_zones_settings = device.thermostat_zones_settings();
    }
    serialize_params() {
        return { zone_no: this.zone_no, color: this._color };
    }
    is_same(other) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        return super.is_same(...arguments) && this.zone_no === other.zone_no;
    }
    title() {
        return `расчётная t° ТН${zone_suffix(this._multiple_zones, this._control_therm, this.zone_no, this._thermostat_zones_settings)}`;
    }
    color() {
        if (this._color)
            return this._color;
        return heatsettempcolors[this.zone_no - 1];
    }
    data_source_key() {
        return LoadDataType.heatsettemp;
    }
    yaxis_key() {
        return 'temperature';
    }
    extract_data(raw_data) {
        ////////?????????????????//
        return raw_data?.[this.zone_no];
    }
    normalize_data(data) {
        return make_staircase(time_mul_1k(normalize_time(data, 0)));
    }
}
